/**
 * Reducer to manage contacts related state
 */

import { ActionTypes as types } from '../constants'
import _ from 'lodash'

const initialState = {
  contacts: [],
  topContacts: [],
  otherContacts: [],
  topSendContacts: [],
  topReceiveContacts: [],
  searchedContacts: [],
  blockedContacts: [],
  lastUpdatedContact: {},
  hasFetchedContacts: false,
  hasFetchedBlockedContacts: false,
  activityData: {},
  hasUpdatedContacts: false,
  // for directory search
  isSearchingPeers: false,
  isLoadingPeers: false,
  nonDirectContacts: [],
  nonDirectPageNumber: 0,
  nonDirectTotalPages: 0,
  showSearchError: false,
  // for business search
  isLoadingBusinesses: false,
  bizDirectContacts: [],
  bizNonDirectContacts: [],
  bizNonDirectPageNumber: 0,
  bizNonDirectTotalPages: 0,
}

export default (state = initialState, action) => {
  switch (action.type) {
    case types.CONTACTS.RECEIVED_CONTACTS:
      let topSendContacts = state.topSendContacts
      let topReceiveContacts = state.topReceiveContacts

      if (action.data.topSendContacts && action.data.topReceiveContacts) {
        topSendContacts = [...action.data.topSendContacts]
        topReceiveContacts = [...action.data.topReceiveContacts]
      }

      return {
        ...state,
        contacts: [...action.data.topContacts, ...action.data.otherContacts],
        topContacts: action.data.topContacts,
        otherContacts: action.data.otherContacts,
        topSendContacts: topSendContacts,
        topReceiveContacts: topReceiveContacts,
        hasFetchedContacts: true,
        hasUpdatedContacts: false,
      }
    case types.CONTACTS.RECEIVED_SEARCHED_CONTACTS:
      return {
        ...state,
        searchedContacts: _.differenceBy(
          _.unionBy(
            state.searchedContacts,
            [...action.data.topContacts, ...action.data.otherContacts],
            'id'
          ),
          state.contacts,
          'id'
        ),
      }
    case types.PEERS.RECEIVED_SEARCHED_PEERS:
      return {
        ...state,
        directContacts: action.data.directContacts,
        nonDirectPageNumber: 2,
        nonDirectTotalPages: action.data.nonDirectTotalPages,
        nonDirectContacts: action.data.nonDirectContacts,
        showSearchError: action.data.showSearchError,
        isSearchingPeers: false,
        bizDirectContacts: action.data.bizDirectContacts,
        bizNonDirectPageNumber: 2,
        bizNonDirectTotalPages: action.data.bizNonDirectTotalPages,
        bizNonDirectContacts: action.data.bizNonDirectContacts,
      }
    case types.PEERS.RECEIVED_LOADED_PEERS:
      return {
        ...state,
        nonDirectPageNumber:
          (state.nonDirectPageNumber ? state.nonDirectPageNumber : 1) + 1,
        nonDirectContacts: [
          ...state.nonDirectContacts,
          ...action.data.nonDirectContacts,
        ],
        isLoadingPeers: false,
        showSearchError: action.data.showSearchError,
      }
    case types.PEERS.RECEIVED_LOADED_BUSINESSES:
      return {
        ...state,
        bizNonDirectPageNumber:
          (state.bizNonDirectPageNumber ? state.bizNonDirectPageNumber : 1) + 1,
        bizNonDirectContacts: [
          ...state.bizNonDirectContacts,
          ...action.data.bizNonDirectContacts,
        ],
        isLoadingBusinesses: false,
        showSearchError: action.data.showSearchError,
      }
    case types.PEERS.SEARCH_ERROR:
      return {
        ...state,
        showSearchError: true,
        isSearchingPeers: false,
        isLoadingPeers: false,
        isLoadingBusinesses: false,
      }
    case types.CONTACTS.RECEIVED_BLOCKED_CONTACTS:
      return {
        ...state,
        hasFetchedBlockedContacts: true,
        blockedContacts: action.data.blockedContacts,
      }
    case types.CONTACTS.RECEIVED_CONTACT_DATA:
      return {
        ...state,
        activityData: {
          ...state.activityData,
          ...action.data,
        },
      }
    case types.CONTACTS.DELETED_CONTACT:
      const {
        data: { contactId },
      } = action
      const topContacts = state.topContacts.filter(c => c.id !== contactId)
      const otherContacts = state.otherContacts.filter(c => c.id !== contactId)

      return {
        ...state,
        contacts: [...topContacts, ...otherContacts],
        topContacts,
        otherContacts,
        topSendContacts: state.topSendContacts.filter(c => c.id !== contactId),
        topReceiveContacts: state.topReceiveContacts.filter(
          c => c.id !== contactId
        ),
      }
    case types.CONTACTS.FAVORITED_CONTACT:
      const {
        data: { isFavorite },
      } = action

      let hasContactBeenUpdated = false

      // Go find and update the contact if it exists in the topContacts array
      const topContactsArray = state.topContacts.map(contact => {
        if (contact.id !== action.data.contactId) {
          return contact
        }

        hasContactBeenUpdated = true

        return {
          ...contact,
          isFavorite,
        }
      })

      // Go find and update the contact if it exists in the otherContacts array
      const otherContactsArray = state.otherContacts.map(contact => {
        if (contact.id !== action.data.contactId) {
          return contact
        }

        hasContactBeenUpdated = true

        return {
          ...contact,
          isFavorite,
        }
      })

      // Go find and update the contact if it exists in the topSendContacts array
      const topSendContactsArray = state.topSendContacts.map(contact => {
        if (contact.id !== action.data.contactId) {
          return contact
        }

        hasContactBeenUpdated = true

        return {
          ...contact,
          isFavorite,
        }
      })

      // Go find and update the contact if it exists in the topReceiveContacts array
      const topReceiveContactsArray = state.topReceiveContacts.map(contact => {
        if (contact.id !== action.data.contactId) {
          return contact
        }

        hasContactBeenUpdated = true

        return {
          ...contact,
          isFavorite,
        }
      })

      return {
        ...state,
        contacts: [...topContactsArray, ...otherContactsArray],
        topContacts: topContactsArray,
        otherContacts: otherContactsArray,
        topSendContacts: topSendContactsArray,
        topReceiveContacts: topReceiveContactsArray,
        hasUpdatedContacts: hasContactBeenUpdated,
      }

    case types.TRANSACTION.CANCELLED_TRANSACTION:
    case types.TRANSACTION.LEAVING_SUCCESS_PAGE:
      return {
        ...state,
        hasFetchedContacts: false,
        hasFetchedBlockedContacts: false,
        activityData: {},
      }

    case types.CONTACTS.UPDATED_CONTACT_BLOCK:
      const { data: { id = '', isBlock = false } = {} } = action

      if (isBlock) {
        const blockedContact = [
          ...state.contacts,
          ...state.searchedContacts,
        ].find(contact => contact.id === id)

        return {
          ...state,
          lastUpdatedContact: blockedContact,
          contacts: state.contacts.filter(contact => contact.id !== id),
          topContacts: state.topContacts.filter(contact => contact.id !== id),
          otherContacts: state.otherContacts.filter(
            contact => contact.id !== id
          ),
          searchedContacts: state.searchedContacts.filter(
            contact => contact.id !== id
          ),
          topSendContacts: state.topSendContacts.filter(
            contact => contact.id !== id
          ),
          topReceiveContacts: state.topReceiveContacts.filter(
            contact => contact.id !== id
          ),
          blockedContacts: _.sortBy(
            _.unionBy(state.blockedContacts, [blockedContact], 'id'),
            contact => contact.alternateFullName || contact.fullName
          ),
        }
      }

      const unblockedContact = state.blockedContacts.find(
        contact => contact.id === id
      )

      return {
        ...state,
        lastUpdatedContact: unblockedContact,
        blockedContacts: state.blockedContacts.filter(
          contact => contact.id !== id
        ),
        hasUpdatedContacts: true,
      }

    case types.PEERS.SEARCHING_PEERS:
      return {
        ...state,
        isSearchingPeers: true,
        nonDirectContacts: [],
        bizNonDirectContacts: [],
      }
    case types.PEERS.LOADING_PEERS:
      return {
        ...state,
        isLoadingPeers: true,
      }
    case types.PEERS.LOADING_BUSINESSES:
      return {
        ...state,
        isLoadingBusinesses: true,
      }
    case types.PEERS.RETRIEVED_PEERS:
      return {
        ...state,
        isSearchingPeers: false,
        isLoadingPeers: false,
        isLoadingBusinesses: false,
      }
    default:
      return state
  }
}
